<!--
  @component StatusPill — the unified-taxonomy badge.
  tier:      primitive (visual atom, no IO hooks)
  purpose:   one-line origin + (optional) stage + (optional) code,
             color-coded by severity. The visual representative of
             the j-arc unified failure shape (origin, stage, code).
-->
<script lang="ts">
  interface Props {
    /** Origin from the unified taxonomy: e.g. "ok", "runtime",
     *  "domain", "config", "circuit". */
    origin: string;
    /** Optional stage facet (per-arc; e.g. "cutover", "supply"). */
    stage?: string;
    /** Optional error/op code, e.g. "ROLLBACK_STAGE_FAILED". */
    code?: string;
    /** Explicit severity override; otherwise derived from origin. */
    severity?: "ok" | "warn" | "error" | "info";
    /** Compact form: just origin (no stage/code shown). */
    compact?: boolean;
  }
  const { origin, stage, code, severity, compact = false }: Props = $props();

  function deriveSeverity(o: string): "ok" | "warn" | "error" | "info" {
    if (o === "ok") return "ok";
    if (o === "runtime" || o === "circuit") return "error";
    if (o === "domain") return "warn";
    return "info";
  }

  const sev = $derived(severity ?? deriveSeverity(origin));
</script>

<span class="pill" data-severity={sev} role="status">
  <span class="origin">{origin}</span>
  {#if !compact && stage}
    <span class="sep">·</span><span class="stage">{stage}</span>
  {/if}
  {#if !compact && code}
    <span class="sep">·</span><code>{code}</code>
  {/if}
</span>

<style>
  .pill {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 2px var(--space-2);
    border-radius: var(--radius-pill);
    font-family: var(--type-font);
    font-size: 11px;
    font-weight: 500;
    letter-spacing: 0.02em;
    border: 0.5px solid var(--border);
    background: var(--glass-fill);
    color: var(--text-primary);
    line-height: 1.6;
    vertical-align: middle;
  }
  .pill[data-severity="ok"] {
    background: var(--success-subtle);
    color: var(--success);
    border-color: var(--success-subtle);
  }
  .pill[data-severity="warn"] {
    background: var(--warning-subtle);
    color: var(--warning);
    border-color: var(--warning-subtle);
  }
  .pill[data-severity="error"] {
    background: var(--error-subtle);
    color: var(--error);
    border-color: var(--error-subtle);
  }
  .pill[data-severity="info"] {
    background: var(--accent-subtle);
    color: var(--accent-text);
    border-color: var(--accent-border);
  }
  .origin {
    text-transform: lowercase;
  }
  .sep {
    opacity: 0.5;
  }
  .stage {
    opacity: 0.85;
  }
  code {
    font-family: var(--type-mono);
    font-size: 10px;
    opacity: 0.85;
  }
</style>
